
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';
import { MfaFactor } from '../../types';
import { useBaseMfaState } from './useBaseMfaState';
import { handleMfaError } from '../types';

/**
 * Hook for managing MFA factors
 * 
 * Provides functionality to fetch and manage a user's enrolled MFA factors
 * 
 * @returns MFA factors state and management functions
 */
export function useMfaFactors() {
  const { 
    setIsLoading, 
    enrolledFactors, 
    setEnrolledFactors 
  } = useBaseMfaState();

  /**
   * Fetch the user's enrolled MFA factors from the database
   * 
   * This function:
   * 1. Requests the user's factors from our custom database table
   * 2. Maps the response to our application's MFA factor type
   * 3. Updates the enrolled factors state
   * 4. Handles errors with user feedback
   * 
   * @returns Promise resolving to array of enrolled MFA factors
   */
  const fetchMfaFactors = async (): Promise<MfaFactor[]> => {
    try {
      setIsLoading(true);
      
      // Fetch from our custom mfa_factors table
      const { data: factorsData, error } = await supabase
        .from('mfa_factors')
        .select('*')
        .eq('status', 'verified');
      
      if (error) throw error;
      
      // Map the database response to our MfaFactor type
      const mappedFactors: MfaFactor[] = (factorsData || []).map(factor => ({
        id: factor.id,
        type: 'totp',
        factorType: factor.factor_type,
        status: factor.status,
        createdAt: factor.created_at,
        updatedAt: factor.updated_at,
        friendlyName: factor.friendly_name
      }));
      
      setEnrolledFactors(mappedFactors);
      return mappedFactors;
    } catch (error) {
      const mfaError = handleMfaError(error);
      console.error('Failed to fetch MFA factors:', mfaError);
      toast({
        title: "MFA Error",
        description: mfaError.message || "Failed to fetch MFA factors",
        variant: "destructive"
      });
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  return {
    enrolledFactors,
    fetchMfaFactors
  };
}
